import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, UserPlus, Trash2, Mail, Copy } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";

function generatePassword(len = 12) {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let p = "";
    for (let i = 0; i < len; i += 1) p += chars[Math.floor(Math.random() * chars.length)];
    return p + "!";
}

export default function CustomerUsersPanel({ customerId, customerName }) {
    const [users, setUsers] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: generatePassword() });
    const [submitting, setSubmitting] = useState(false);
    const [createdCreds, setCreatedCreds] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/customers/${customerId}/users`);
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const create = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post(`/customers/${customerId}/users`, {
                name: form.name,
                email: form.email,
                password: form.password,
            });
            // Show one-time credential confirmation
            setCreatedCreds({ email: form.email, password: form.password, name: form.name });
            toast.success("Account aangemaakt — bewaar de inloggegevens nu.");
            setOpen(false);
            setForm({ name: "", email: "", password: generatePassword() });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (userId) => {
        try {
            await api.delete(`/users/${userId}`);
            toast.success("Gebruiker verwijderd");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const copyCreds = () => {
        if (!createdCreds) return;
        const text = `${customerName} — Social Factory Portal\nE-mail: ${createdCreds.email}\nWachtwoord: ${createdCreds.password}\nLogin: https://auto-catalog-hub.emergent.host/login`;
        navigator.clipboard?.writeText(text);
        toast.success("Inloggegevens gekopieerd");
    };

    return (
        <div className="bg-white border border-zinc-200 rounded-md p-6 mb-10" data-testid="customer-users-panel">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
                        Toegang
                    </div>
                    <h2 className="font-display text-2xl tracking-tight font-bold">
                        {users?.length || 0} gebruiker{users?.length === 1 ? "" : "s"}
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1">
                        Mensen van deze klant die mogen inloggen op de portal.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                            data-testid="new-user-button"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Account aanmaken
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-display tracking-tight text-2xl">
                                Account voor {customerName}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={create} className="space-y-4" data-testid="new-user-form">
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                    Naam *
                                </label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="new-user-name"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                    E-mail *
                                </label>
                                <Input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="new-user-email"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                    Wachtwoord *
                                </label>
                                <div className="flex gap-2">
                                    <Input
                                        required
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        className="font-mono"
                                        data-testid="new-user-password"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setForm({ ...form, password: generatePassword() })}
                                        title="Nieuw wachtwoord genereren"
                                        data-testid="new-user-regen"
                                    >
                                        Genereer
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mt-1.5">
                                    Bewaar dit wachtwoord — het wordt na aanmaken niet meer getoond.
                                </p>
                            </div>
                            <DialogFooter>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                    data-testid="new-user-submit"
                                >
                                    {submitting ? "Bezig…" : "Account aanmaken"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {!users ? (
                <div className="h-16 bg-zinc-50 border border-zinc-200 rounded-md animate-pulse" />
            ) : users.length === 0 ? (
                <div className="border border-dashed border-zinc-300 rounded-md py-8 text-center text-sm text-zinc-500">
                    Nog geen accounts. Klik "Account aanmaken" om iemand toegang te geven.
                </div>
            ) : (
                <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-md">
                    {users.map((u) => (
                        <div
                            key={u.id}
                            className="flex items-center justify-between gap-4 px-4 py-3"
                            data-testid={`user-row-${u.id}`}
                        >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-9 h-9 rounded-full bg-[#0044FF] text-white flex items-center justify-center font-semibold text-sm shrink-0">
                                    {(u.name || u.email).slice(0, 1).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{u.name}</div>
                                    <div className="text-xs font-mono text-zinc-500 truncate flex items-center gap-1.5">
                                        <Mail className="w-3 h-3" />
                                        {u.email}
                                    </div>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 hidden md:block">
                                    {new Date(u.created_at).toLocaleDateString("nl-BE")}
                                </div>
                            </div>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <button
                                        className="text-zinc-400 hover:text-red-600 p-1.5 rounded hover:bg-zinc-100"
                                        data-testid={`user-delete-${u.id}`}
                                        aria-label="Verwijderen"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Account verwijderen?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            "{u.name}" ({u.email}) verliest direct de toegang tot
                                            de portal. De bestanden van {customerName} blijven
                                            bewaard.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Annuleer</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => remove(u.id)}
                                            className="bg-red-600 hover:bg-red-700"
                                        >
                                            Verwijderen
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    ))}
                </div>
            )}

            {/* One-time credential confirmation dialog */}
            <Dialog open={!!createdCreds} onOpenChange={(o) => !o && setCreatedCreds(null)}>
                <DialogContent data-testid="user-created-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-tight text-2xl">
                            Account aangemaakt ✓
                        </DialogTitle>
                    </DialogHeader>
                    {createdCreds && (
                        <div className="space-y-4">
                            <p className="text-sm text-zinc-600">
                                Bewaar of stuur deze inloggegevens naar{" "}
                                <span className="font-medium">{createdCreds.name}</span>. Het
                                wachtwoord kan je hierna niet meer ophalen.
                            </p>
                            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 font-mono text-sm space-y-1">
                                <div>
                                    <span className="text-zinc-500">E-mail: </span>
                                    {createdCreds.email}
                                </div>
                                <div>
                                    <span className="text-zinc-500">Wachtwoord: </span>
                                    {createdCreds.password}
                                </div>
                            </div>
                            <Button
                                onClick={copyCreds}
                                variant="outline"
                                className="w-full"
                                data-testid="copy-creds-button"
                            >
                                <Copy className="w-4 h-4 mr-2" />
                                Kopieer inloggegevens
                            </Button>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            onClick={() => setCreatedCreds(null)}
                            className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                        >
                            Klaar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
